import type { Profile } from '@/types';

interface MemberRowProps {
  member: Profile;
  busy: boolean;
  onApprove: (id: string) => void;
  onReject: (id: string) => void;
  onReset: (id: string) => void;
}

const statusBadge = (status: Profile['status']) => {
  if (status === 'approved') return 'bg-primary-100 text-primary-700';
  if (status === 'rejected') return 'bg-accent-100 text-accent-800';
  return 'bg-secondary-200 text-secondary-900';
};

const statusLabel = (status: Profile['status']) => {
  if (status === 'approved') return '승인 완료';
  if (status === 'rejected') return '승인 거절';
  return '승인 대기';
};

function formatDate(value: string) {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '-';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}.${m}.${day}`;
}

export default function MemberRow({
  member,
  busy,
  onApprove,
  onReject,
  onReset,
}: MemberRowProps) {
  const initial = (member.name || member.email || '?').trim().charAt(0).toUpperCase();
  const isAdmin = member.role === 'admin';

  return (
    <div className="flex flex-col gap-4 rounded-lg border border-background-200 bg-background-50 p-4 md:flex-row md:items-center md:justify-between md:p-5">
      <div className="flex min-w-0 items-center gap-3">
        <span
          className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-base font-bold ${
            isAdmin
              ? 'bg-primary-500 text-background-50'
              : 'bg-secondary-100 text-secondary-900'
          }`}
        >
          {initial}
        </span>
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <p className="truncate text-sm font-semibold text-foreground-950">
              {member.name || '이름 없음'}
            </p>
            {isAdmin && (
              <span className="flex items-center gap-1 rounded-full bg-primary-100 px-2 py-0.5 text-xs font-semibold text-primary-700">
                <i className="ri-shield-star-line"></i>
                운영자
              </span>
            )}
          </div>
          <p className="truncate text-xs text-foreground-500">{member.email || '이메일 없음'}</p>
          <p className="mt-0.5 flex items-center gap-1 text-xs text-foreground-400">
            <i className="ri-calendar-line"></i>
            가입일 {formatDate(member.created_at)}
          </p>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 md:justify-end">
        <span className={`rounded-full px-3 py-1 text-xs font-semibold ${statusBadge(member.status)}`}>
          {statusLabel(member.status)}
        </span>

        {!isAdmin && (
          <div className="flex flex-wrap gap-2">
            {member.status !== 'approved' && (
              <button
                type="button"
                disabled={busy}
                onClick={() => onApprove(member.id)}
                className="flex items-center gap-1.5 rounded-md bg-primary-500 px-3 py-2 text-xs font-semibold text-background-50 transition hover:bg-primary-600 disabled:opacity-50 whitespace-nowrap"
              >
                <i className="ri-check-line"></i>
                승인
              </button>
            )}
            {member.status !== 'rejected' && (
              <button
                type="button"
                disabled={busy}
                onClick={() => onReject(member.id)}
                className="flex items-center gap-1.5 rounded-md border border-background-300 px-3 py-2 text-xs font-semibold text-foreground-700 transition hover:bg-background-100 disabled:opacity-50 whitespace-nowrap"
              >
                <i className="ri-close-line"></i>
                거절
              </button>
            )}
            {member.status !== 'pending' && (
              <button
                type="button"
                disabled={busy}
                onClick={() => onReset(member.id)}
                className="flex items-center gap-1.5 rounded-md px-3 py-2 text-xs font-semibold text-foreground-500 transition hover:bg-background-100 hover:text-foreground-800 disabled:opacity-50 whitespace-nowrap"
              >
                <i className="ri-arrow-go-back-line"></i>
                대기로
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}